import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../../app/store'
import config from '../../../config'
import { sleep } from '../../../shared/utils/async'
import { debounce, fallbackCopyTextToClipboard } from '../../../shared/utils/basic'
import {
    mpTrackButtonClick,
    mpTrackArticleEnd,
    mpTrackIntervalListen,
    mpTrackVolumeChange,
    mpTrackPlaybackSpeedChange,
} from '../../../tracking/mixpanel'
import MpTrackingButtons from '../../../tracking/types/mpTrackingButtons'
import { transformArticleFromResponse, transformAudioDuration } from '../transforms'
import { IArticle } from '../types/IArticle'
import { IArticleTransformed } from '../types/IArticleTransformed'
import { IAudioDuration } from '../types/IAudioDuration'
import { fetchArticleById, fetchArticles } from './articleAPI'

const TRACKING_LISTEN_INCREMENT = 10

export interface ArticleState {
    articles: IArticleTransformed[]
    currentlyPlayingArticle?: IArticleTransformed
    status: 'idle' | 'loading' | 'failed'
    seekbarPosition: number
    audioDuration: number
    jumpToSeekbarPosition?: number
    audioState: 'idle' | 'playing' | 'paused'
    audioSpeed: number
    isManualSeeking: boolean
    lastAudioIncrementTracked: number
    showCopyToClipboardToast: boolean
    volume: number

    // Recently Played or Paused
    lastPlayedArticleTimestamp?: number
}

const initialState: ArticleState = {
    articles: [],
    currentlyPlayingArticle: undefined,
    status: 'idle',
    seekbarPosition: 0,
    audioDuration: 300,
    jumpToSeekbarPosition: undefined,
    audioState: 'idle',
    audioSpeed: 1,
    isManualSeeking: false,
    lastAudioIncrementTracked: -TRACKING_LISTEN_INCREMENT,
    showCopyToClipboardToast: false,
    volume: 1,
    lastPlayedArticleTimestamp: undefined,
}

export const fetchArticleByIdThunk = createAsyncThunk('article/fetchArticleById', async () => {
    const searchParams = new URLSearchParams(window.location.search)
    const publisherKey = searchParams.get('key')
    const articleId = searchParams.get('id')

    const response = await fetchArticleById({
        publisherKey,
        articleId,
    })

    return response
})

export const fetchArticlesThunk = createAsyncThunk('article/fetchArticles', async () => {
    const searchParams = new URLSearchParams(window.location.search)
    const publisherKey = searchParams.get('key')
    const limit = searchParams.get('limit')
    const articleIds = searchParams.get('articleIds')

    const response = await fetchArticles({
        publisherKey,
        limit,
        articleIds,
    })

    return response
})

export const copyToClipboardToastThunk = createAsyncThunk('article/showCopyToClipboardToast', async () => {
    await sleep({ ms: 5 * 1000 })

    return true
})

const setMpTrackVolumeChange = debounce((volume: number, currentlyPlayingArticle?: IArticleTransformed) =>
    mpTrackVolumeChange(volume, currentlyPlayingArticle)
)

export const articleSlice = createSlice({
    name: 'article',
    initialState,
    reducers: {
        setArticles: (state, action: PayloadAction<IArticle[]>) => {
            const articlesTransformed = action.payload.map(transformArticleFromResponse)

            state.status = 'idle'
            state.articles = articlesTransformed
            state.audioState = 'idle'

            if (!state.currentlyPlayingArticle) {
                state.currentlyPlayingArticle = articlesTransformed[0]
                state.seekbarPosition = 0
            } else {
                // if recently playing article was stored in the localStorage then seek to that timestamp
                state.jumpToSeekbarPosition = state.seekbarPosition
            }
        },
        manualSeekTo: (state, action: PayloadAction<number>) => {
            if (!state.isManualSeeking) {
                state.isManualSeeking = true
            }
            state.seekbarPosition = action.payload
        },

        manualSeekToComplete: (state, action: PayloadAction<number>) => {
            if (state.isManualSeeking) {
                state.isManualSeeking = false
            }
            state.jumpToSeekbarPosition = action.payload
            mpTrackButtonClick(MpTrackingButtons.SCRUB, state.currentlyPlayingArticle)
        },

        setAudioPlayerProgressed: (state, action: PayloadAction<number>) => {
            if (state.isManualSeeking) {
                return
            }

            state.seekbarPosition = action.payload

            const nextAudioIncrementToTrack = state.lastAudioIncrementTracked + TRACKING_LISTEN_INCREMENT

            if (state.seekbarPosition > nextAudioIncrementToTrack) {
                const intervalToTrack = Math.floor(state.seekbarPosition / 10) * 10
                mpTrackIntervalListen(intervalToTrack, state.currentlyPlayingArticle)
                state.lastAudioIncrementTracked = intervalToTrack
            }
        },

        setAudioDuration: (state, action: PayloadAction<number>) => {
            state.audioDuration = action.payload
        },

        setVolume: (state, action: PayloadAction<number>) => {
            setMpTrackVolumeChange(action.payload, JSON.parse(JSON.stringify(state.currentlyPlayingArticle)))
            state.volume = action.payload
        },

        handleAudioComplete: (state, action: PayloadAction<IArticleTransformed>) => {
            // First track existing article
            mpTrackArticleEnd(state.seekbarPosition, state.currentlyPlayingArticle)

            state.jumpToSeekbarPosition = 0
            state.lastAudioIncrementTracked = -TRACKING_LISTEN_INCREMENT
            state.audioState = 'playing'

            const indexOfCurrentArticle = state.articles.findIndex(a => a.id === action.payload.id)
            let indexOfNextArticle = (indexOfCurrentArticle + 1) % state.articles.length

            state.currentlyPlayingArticle = state.articles[indexOfNextArticle]
        },

        setArticleOnAudioComplete: (state, action: PayloadAction<IArticleTransformed>) => {
            // First track existing article
            mpTrackArticleEnd(state.seekbarPosition, state.currentlyPlayingArticle)

            state.jumpToSeekbarPosition = 0
            state.lastAudioIncrementTracked = -TRACKING_LISTEN_INCREMENT
            state.audioState = 'playing'
            state.currentlyPlayingArticle = { ...action.payload }
        },

        setAudioSpeed: (state, action: PayloadAction<number>) => {
            mpTrackPlaybackSpeedChange(action.payload, state.currentlyPlayingArticle)
            state.audioSpeed = action.payload
        },

        seek15SecsBack: state => {
            state.jumpToSeekbarPosition = Math.max(0, state.seekbarPosition - 15)
            state.seekbarPosition = Math.max(0, state.seekbarPosition - 15)
        },

        clearJumpToSeekbarPosition: state => {
            state.jumpToSeekbarPosition = undefined
        },

        playOrPauseArticle: (state, action: PayloadAction<IArticleTransformed | undefined>) => {
            state.lastPlayedArticleTimestamp = new Date().getTime()

            // if the payload is new article then update the currently playingArticle
            if (
                action.payload &&
                (state.currentlyPlayingArticle?.id !== action.payload.id ||
                    state.currentlyPlayingArticle.queueId !== action.payload.queueId)
            ) {
                mpTrackArticleEnd(state.seekbarPosition, state.currentlyPlayingArticle)
                mpTrackButtonClick(MpTrackingButtons.PLAY, action.payload)

                state.jumpToSeekbarPosition = 0
                state.lastAudioIncrementTracked = -TRACKING_LISTEN_INCREMENT
                state.audioState = 'playing'
                state.currentlyPlayingArticle = action.payload
            }

            // Play or pause existing article
            else {
                if (state.audioState === 'playing') {
                    state.audioState = 'paused'
                    mpTrackButtonClick(MpTrackingButtons.PAUSE, state.currentlyPlayingArticle)
                } else {
                    state.audioState = 'playing'
                    mpTrackButtonClick(MpTrackingButtons.PLAY, state.currentlyPlayingArticle)
                }
                return
            }
        },
        resetArticleStorage: state => {
            state.seekbarPosition = 0
            state.jumpToSeekbarPosition = 0
            state.audioState = 'idle'
            state.lastAudioIncrementTracked = -TRACKING_LISTEN_INCREMENT
            state.currentlyPlayingArticle = state.articles[0]
            state.lastPlayedArticleTimestamp = new Date().getTime()
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchArticlesThunk.pending, state => {
                state.status = 'loading'
            })
            .addCase(fetchArticlesThunk.fulfilled, (state, action) => {
                const articlesTransformed = action.payload.map(transformArticleFromResponse)

                state.status = 'idle'
                state.articles = articlesTransformed
                state.audioState = 'idle'

                if (!state.currentlyPlayingArticle) {
                    state.currentlyPlayingArticle = articlesTransformed[0]
                    state.seekbarPosition = 0
                } else {
                    // if recently playing article was stored in the localStorage then seek to that timestamp
                    state.jumpToSeekbarPosition = state.seekbarPosition
                }
            })
            .addCase(fetchArticlesThunk.rejected, state => {
                state.status = 'failed'
            })
            .addCase(fetchArticleByIdThunk.pending, state => {
                state.status = 'loading'
            })
            .addCase(fetchArticleByIdThunk.fulfilled, (state, action) => {
                const articlesTransformed = transformArticleFromResponse(action.payload)

                state.status = 'idle'
                state.articles = [articlesTransformed]
                state.audioState = 'idle'

                if (!state.currentlyPlayingArticle) {
                    state.currentlyPlayingArticle = articlesTransformed
                    state.seekbarPosition = 0
                } else {
                    // if recently playing article was stored in the localStorage then seek to that timestamp
                    state.jumpToSeekbarPosition = state.seekbarPosition
                }
            })
            .addCase(fetchArticleByIdThunk.rejected, state => {
                state.status = 'failed'
            })

            .addCase(copyToClipboardToastThunk.pending, state => {
                state.showCopyToClipboardToast = true

                if (
                    state.currentlyPlayingArticle &&
                    state.currentlyPlayingArticle.shareUrl &&
                    state.currentlyPlayingArticle.publisher.name
                ) {
                    const urlToCopy = `${state.currentlyPlayingArticle.shareUrl}&umt_medium=embed&utm_source=${state.currentlyPlayingArticle.publisher.name}&utm_campaign=sidebar_playlist`
                    try {
                        navigator.clipboard.writeText(urlToCopy)
                    } catch {
                        fallbackCopyTextToClipboard(urlToCopy)
                    }
                }

                mpTrackButtonClick(MpTrackingButtons.SHARE, state.currentlyPlayingArticle)
            })
            .addCase(copyToClipboardToastThunk.fulfilled, state => {
                state.showCopyToClipboardToast = false
            })
            .addCase(copyToClipboardToastThunk.rejected, state => {
                state.showCopyToClipboardToast = false
            })
    },
})

export const {
    manualSeekTo,
    manualSeekToComplete,
    setAudioPlayerProgressed,
    clearJumpToSeekbarPosition,
    setAudioDuration,
    playOrPauseArticle,
    setAudioSpeed,
    handleAudioComplete,
    setArticleOnAudioComplete,
    seek15SecsBack,
    setVolume,
    resetArticleStorage,
    setArticles,
} = articleSlice.actions

export const selectAllArticles = (state: RootState) => state.article.articles
export const selectCurrentlyPlayingArticle = (state: RootState) => state.article.currentlyPlayingArticle
export const selectAudioState = (state: RootState) => state.article.audioState
export const selectSeekbarPosition = (state: RootState) =>
    transformAudioDuration({
        audioDurationSeconds: state.article.seekbarPosition,
    })
export const selectJumpToSeekbarPosition = (state: RootState) => state.article.jumpToSeekbarPosition
export const selectAudioDuration = (state: RootState): IAudioDuration =>
    transformAudioDuration({
        audioDurationSeconds: state.article.audioDuration,
    })
export const selectPlayedArticleTimestamp = (state: RootState) => state.article.lastPlayedArticleTimestamp
export const selectVolume = (state: RootState): number => state.article.volume
export const selectAudioSpeed = (state: RootState): number => state.article.audioSpeed
export const selectShowCopyToClipboard = (state: RootState): boolean => state.article.showCopyToClipboardToast
export const selectLinkToNoa = (state: RootState) => {
    const { currentlyPlayingArticle } = state.article

    if (!currentlyPlayingArticle || !currentlyPlayingArticle.offPlatformToNoaLink) {
        return config.websiteUrl
    }

    const linkWithUtm = `${currentlyPlayingArticle.offPlatformToNoaLink}&utm_source=playlist-widget`

    return linkWithUtm
}

export default articleSlice.reducer
